/**
 * Cliente HTTP do Terminal Kiosk — F8.2 (ADR-0023) + Producao v2 (P3).
 *
 * Auth via X-Terminal-Token (NAO JWT). Token armazenado em
 * sessionStorage (sai ao fechar aba). Fetch direto contra a API
 * configurada em REACT_APP_ACCESS_API_URL.
 *
 * P3 (corte unico, decisao 4 da gestora): o terminal passa a operar o
 * fluxo v2 PECA x POSTO — login por posto (dropdown publico de postos
 * ativos), contexto-peca e bipar v2 (ENTROU_FILA/INICIAR/CONCLUIR/
 * PAUSAR/RETOMAR + qtd boa/refugo + setup + nao-conformidades). As
 * funcoes do fluxo v1 (OP x estacao) seguem exportadas por retrocompat
 * do backend, mas a UI nao as usa mais.
 *
 * Como o Terminal nao usa AuthContext, este modulo cuida sozinho do
 * baseUrl + headers + serializacao.
 */
import { ACCESS_API_URL } from "../../config/api";

const STORAGE_KEY_TOKEN = "pharuerp.terminal.token";
const STORAGE_KEY_EXPIRA = "pharuerp.terminal.expira_em";
const STORAGE_KEY_OPERADOR = "pharuerp.terminal.operador_nome";
const STORAGE_KEY_ESTACAO = "pharuerp.terminal.estacao";
const STORAGE_KEY_UNIDADE = "pharuerp.terminal.unidade_id";
const STORAGE_KEY_SUPERVISOR = "pharuerp.terminal.eh_supervisor";
// Producao v2 (P3) — sessao por posto
const STORAGE_KEY_POSTO_ID = "pharuerp.terminal.posto_id";
const STORAGE_KEY_POSTO_NUMERO = "pharuerp.terminal.posto_numero";
const STORAGE_KEY_POSTO_NOME = "pharuerp.terminal.posto_nome";
const STORAGE_KEY_ETAPA_CODIGO = "pharuerp.terminal.etapa_codigo";
const STORAGE_KEY_ETAPA_LABEL = "pharuerp.terminal.etapa_label";

// Configuracao DO APARELHO, nao da sessao: qual posto fisico este tablet
// atende. Vive em localStorage porque precisa sobreviver ao logout e ao
// fechamento da aba — o operador digita so o PIN no dia a dia. Substitui
// os dropdowns que eram alimentados por listas publicas de unidades e
// postos (removidas do backend em 2026-08).
const STORAGE_KEY_POSTO_CONFIG = "pharuerp.terminal.posto_configurado";

export function lerPostoConfigurado() {
  try {
    return localStorage.getItem(STORAGE_KEY_POSTO_CONFIG) || "";
  } catch {
    return "";
  }
}

export function salvarPostoConfigurado(numero) {
  try {
    localStorage.setItem(STORAGE_KEY_POSTO_CONFIG, String(numero || "").trim());
  } catch {
    // Modo privado / storage cheio: o terminal segue funcionando, so
    // pede o numero do posto de novo no proximo login.
  }
}

export function limparPostoConfigurado() {
  try {
    localStorage.removeItem(STORAGE_KEY_POSTO_CONFIG);
  } catch {
    // idem
  }
}

function baseUrl() {
  // Mesma regra do config/api: nada de process.env direto. Com a env
  // ausente no build, o kiosk inteiro passava a chamar URL relativa
  // (CloudFront) e a bipagem morria em silencio.
  return ACCESS_API_URL.replace(/\/+$/, "");
}

function _headers(token) {
  const h = { "Content-Type": "application/json" };
  if (token) h["X-Terminal-Token"] = token;
  return h;
}

async function _parse(resp) {
  const text = await resp.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function fetchSemAuth(path, init = {}) {
  const resp = await fetch(`${baseUrl()}${path}`, {
    ...init,
    headers: { ..._headers(null), ...(init.headers || {}) },
  });
  const data = await _parse(resp);
  if (!resp.ok) {
    const msg = data?.detalhe || data?.detail || `Erro ${resp.status}`;
    const err = new Error(msg);
    err.status = resp.status;
    err.payload = data;
    throw err;
  }
  return data;
}

async function fetchAuth(path, init = {}) {
  const token = lerToken();
  if (!token) {
    const err = new Error("Sessao expirada — faca login novamente.");
    err.status = 401;
    throw err;
  }
  const resp = await fetch(`${baseUrl()}${path}`, {
    ...init,
    headers: { ..._headers(token), ...(init.headers || {}) },
  });
  const data = await _parse(resp);
  if (resp.status === 403 || resp.status === 401) {
    limparSessao();
    const err = new Error(
      data?.detalhe || "Sessao expirada — faca login novamente.",
    );
    err.status = resp.status;
    err.payload = data;
    throw err;
  }
  if (!resp.ok) {
    const msg = data?.detalhe || data?.detail || `Erro ${resp.status}`;
    const err = new Error(msg);
    err.status = resp.status;
    err.payload = data;
    throw err;
  }
  return data;
}

// -------- sessao (sessionStorage) --------

export function lerToken() {
  try {
    return sessionStorage.getItem(STORAGE_KEY_TOKEN);
  } catch {
    return null;
  }
}

export function lerOperadorNome() {
  try {
    return sessionStorage.getItem(STORAGE_KEY_OPERADOR);
  } catch {
    return null;
  }
}

export function lerEstacao() {
  try {
    return sessionStorage.getItem(STORAGE_KEY_ESTACAO);
  } catch {
    return null;
  }
}

export function lerExpiraEm() {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY_EXPIRA);
    return v ? new Date(v) : null;
  } catch {
    return null;
  }
}

export function lerUnidadeId() {
  try {
    return sessionStorage.getItem(STORAGE_KEY_UNIDADE);
  } catch {
    return null;
  }
}

export function lerEhSupervisor() {
  try {
    return sessionStorage.getItem(STORAGE_KEY_SUPERVISOR) === "true";
  } catch {
    return false;
  }
}

/** Posto da sessao v2 (null em sessao antiga por estacao). */
export function lerPosto() {
  try {
    const id = sessionStorage.getItem(STORAGE_KEY_POSTO_ID);
    if (!id) return null;
    return {
      id,
      numero: sessionStorage.getItem(STORAGE_KEY_POSTO_NUMERO) || "",
      nome: sessionStorage.getItem(STORAGE_KEY_POSTO_NOME) || "",
      etapa_codigo: sessionStorage.getItem(STORAGE_KEY_ETAPA_CODIGO) || "",
      etapa_label: sessionStorage.getItem(STORAGE_KEY_ETAPA_LABEL) || "",
    };
  } catch {
    return null;
  }
}

export function temSessaoValida() {
  const token = lerToken();
  const expira = lerExpiraEm();
  if (!token || !expira) return false;
  return expira.getTime() > Date.now();
}

function salvarSessao(payload) {
  try {
    sessionStorage.setItem(STORAGE_KEY_TOKEN, payload.token);
    sessionStorage.setItem(STORAGE_KEY_EXPIRA, payload.expira_em);
    sessionStorage.setItem(STORAGE_KEY_OPERADOR, payload.operador_nome);
    // Sessao v2 (por posto) vem com estacao=null — nunca gravar "null".
    sessionStorage.setItem(STORAGE_KEY_ESTACAO, payload.estacao || "");
    sessionStorage.setItem(STORAGE_KEY_UNIDADE, payload.unidade_id);
    sessionStorage.setItem(
      STORAGE_KEY_SUPERVISOR,
      payload.eh_supervisor ? "true" : "false",
    );
    if (payload.posto) {
      sessionStorage.setItem(STORAGE_KEY_POSTO_ID, payload.posto.id);
      sessionStorage.setItem(STORAGE_KEY_POSTO_NUMERO, payload.posto.numero || "");
      sessionStorage.setItem(STORAGE_KEY_POSTO_NOME, payload.posto.nome || "");
      sessionStorage.setItem(
        STORAGE_KEY_ETAPA_CODIGO, payload.posto.etapa_codigo || "",
      );
      sessionStorage.setItem(
        STORAGE_KEY_ETAPA_LABEL, payload.posto.etapa_label || "",
      );
    }
  } catch {
    // sessionStorage indisponivel — caller percebe quando temSessaoValida falhar
  }
}

export function limparSessao() {
  try {
    sessionStorage.removeItem(STORAGE_KEY_TOKEN);
    sessionStorage.removeItem(STORAGE_KEY_EXPIRA);
    sessionStorage.removeItem(STORAGE_KEY_OPERADOR);
    sessionStorage.removeItem(STORAGE_KEY_ESTACAO);
    sessionStorage.removeItem(STORAGE_KEY_UNIDADE);
    sessionStorage.removeItem(STORAGE_KEY_SUPERVISOR);
    sessionStorage.removeItem(STORAGE_KEY_POSTO_ID);
    sessionStorage.removeItem(STORAGE_KEY_POSTO_NUMERO);
    sessionStorage.removeItem(STORAGE_KEY_POSTO_NOME);
    sessionStorage.removeItem(STORAGE_KEY_ETAPA_CODIGO);
    sessionStorage.removeItem(STORAGE_KEY_ETAPA_LABEL);
  } catch {
    // ignore
  }
}

// -------- API publica --------

/**
 * Garante que a resposta seja sempre Array, mesmo se backend retornar:
 *   - Array direto (esperado)
 *   - {results: [...], count, next, previous} (paginado DRF)
 *   - Objeto qualquer / null / undefined (erro / contrato divergente)
 *
 * Isso protege a UI de quebrar com `unidades.map is not a function`
 * caso config de paginacao mude no backend ou endpoint retorne erro
 * com body inesperado.
 */
function _comoArray(data, label) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && typeof data === "object") {
    // eslint-disable-next-line no-console
    console.warn(
      `[Terminal] Resposta inesperada de ${label} (esperado Array): `,
      data,
    );
  }
  return [];
}

export async function listarEstacoes() {
  const data = await fetchSemAuth("/api/producao/terminal/estacoes");
  return _comoArray(data, "listarEstacoes");
}

export async function login({ pin, estacao, posto, unidade }) {
  // v2 (P3): login por POSTO (id ou numero). `estacao` fica por
  // retrocompat do fluxo v1 — nao usar nos dois ao mesmo tempo.
  //
  // O numero do posto (`POSTO-UN1-3`) ja embute a unidade, entao o
  // backend a resolve sozinho. `unidade` so vai junto no fluxo v1 por
  // estacao, que nao pertence a unidade nenhuma.
  const body = { pin };
  if (unidade) body.unidade = unidade;
  if (posto) body.posto = posto;
  else body.estacao = estacao;
  const data = await fetchSemAuth("/api/producao/terminal/login", {
    method: "POST",
    body: JSON.stringify(body),
  });
  salvarSessao(data);
  return data;
}

export async function logout() {
  try {
    await fetchAuth("/api/producao/terminal/logout", { method: "POST" });
  } catch {
    // ignore: limpar sessao local independente de erro remoto
  }
  limparSessao();
}

export async function contextoOp(numero) {
  return fetchAuth(
    `/api/producao/terminal/contexto-op/${encodeURIComponent(numero)}`,
  );
}

export async function bipar({
  op_numero,
  acao,
  nota_curta,
  override_supervisor = false,
  motivo_override = "",
}) {
  return fetchAuth("/api/producao/bipar", {
    method: "POST",
    body: JSON.stringify({
      op_numero,
      acao,
      nota_curta,
      override_supervisor,
      motivo_override,
    }),
  });
}

// -------- Producao v2 (P3) — peca x posto --------

/**
 * GET /api/producao/terminal/contexto-peca/{numero} — pre-bipagem v2:
 * {peca, fluxo_v2, ambiente, lote_numero, proxima_etapa_bipavel,
 *  meu_posto, compatible, acao_sugerida, mensagem_diagnostico}.
 */
export async function contextoPeca(numero) {
  return fetchAuth(
    `/api/producao/terminal/contexto-peca/${encodeURIComponent(numero)}`,
  );
}

/**
 * POST /api/producao/terminal/bipar — bipagem v2 (posto/etapa vem do
 * token). Retorna {evento, peca, proxima_etapa_apos}.
 * Erros: 404 peca, 409 estado invalido, 422 fora de ordem
 * (payload.etapa_esperada + eh_supervisor), 403 override_requer_supervisor,
 * 400 override_requer_motivo.
 */
export async function biparPeca({
  peca_numero,
  acao,
  qtd_boa,
  qtd_refugo,
  eh_setup = false,
  nao_conformidades,
  nota_curta = "",
  override_supervisor = false,
  motivo_override = "",
}) {
  const body = {
    peca_numero,
    acao,
    eh_setup,
    nota_curta,
    override_supervisor,
    motivo_override,
  };
  if (qtd_boa !== undefined && qtd_boa !== null && qtd_boa !== "") {
    body.qtd_boa = qtd_boa;
  }
  if (qtd_refugo !== undefined && qtd_refugo !== null && qtd_refugo !== "") {
    body.qtd_refugo = qtd_refugo;
  }
  if (Array.isArray(nao_conformidades) && nao_conformidades.length > 0) {
    body.nao_conformidades = nao_conformidades;
  }
  return fetchAuth("/api/producao/terminal/bipar", {
    method: "POST",
    body: JSON.stringify(body),
  });
}
